import React from "react";

import {
  postAdminDisableFrontendAddon,
  postAdminEnableFrontendAddon,
} from "@/api/admin/frontendAddons";
import {
  postDisableFrontendAddon,
  postEnableFrontendAddon,
} from "@/api/frontendAddons";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useBulkActionToast } from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { FrontendAddonRaw } from "@/domain/FrontendAddon";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { DialogPhaseStatesType, useDialog } from "../../../helpers/hooks";

export const DialogFrontendAddonDelete: React.FC<
  {
    frontendAddons: FrontendAddonRaw[];
    selectedFrontendAddonIds: string[];
    setSelectedFrontendAddonIds: React.Dispatch<React.SetStateAction<string[]>>;
    runReloadFrontendAddons: () => Promise<void>;
    preserveSelectionOnCancel?: boolean;
  } & DialogPhaseStatesType
> = ({
  frontendAddons,
  selectedFrontendAddonIds,
  setSelectedFrontendAddonIds,
  dialogPhase,
  setDialogPhase,
  runReloadFrontendAddons,
  preserveSelectionOnCancel = false,
}) => {
  const { organizationId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();

  const selectedEnabledAddons = frontendAddons.filter(
    (addon) => selectedFrontendAddonIds.includes(addon.id) && !addon.is_disabled
  );

  const { renderDialogs } = useDialog({
    dialogPhase,
    setDialogPhase: (phase) => {
      if (phase === null && !preserveSelectionOnCancel) {
        setSelectedFrontendAddonIds([]);
      }
      setDialogPhase(phase);
    },
  });

  const { showErrorNotification } = useAppNotificationManager();

  const { runWithToast } = useBulkActionToast({
    success: (count) =>
      count === 1
        ? "フロントエンドアドオンを無効化しました"
        : `${count}件のフロントエンドアドオンを無効化しました`,
    error: (count) =>
      count === 1
        ? "フロントエンドアドオンを無効化できませんでした"
        : `${count}件のフロントエンドアドオンを無効化できませんでした`,
  });

  const runDelete = async () => {
    setDialogPhase("in_progress");
    try {
      const { failedIds } = await runWithToast(
        selectedEnabledAddons,
        async (addon) => {
          isAADeveloperOrAbove
            ? await postAdminDisableFrontendAddon({
                frontendAddonId: addon.id,
              })
            : await postDisableFrontendAddon({
                organizationId,
                frontendAddonId: addon.id,
              });
        },
        {
          undoItem: async (_result, addon) => {
            isAADeveloperOrAbove
              ? await postAdminEnableFrontendAddon({
                  frontendAddonId: addon.id,
                })
              : await postEnableFrontendAddon({
                  organizationId,
                  frontendAddonId: addon.id,
                });
          },
          onSomeUndoSucceeded: async () => {
            try {
              await runReloadFrontendAddons();
            } catch (error) {
              showErrorNotification(
                "フロントエンドアドオン一覧の更新に失敗しました",
                error
              );
            }
          },
        }
      );
      // 行メニューは選択を借りているだけなので、失敗しても選択に残さない
      setSelectedFrontendAddonIds(preserveSelectionOnCancel ? failedIds : []);
    } finally {
      setDialogPhase(null);
      try {
        await runReloadFrontendAddons();
      } catch (error) {
        showErrorNotification(
          "フロントエンドアドオン一覧の更新に失敗しました",
          error
        );
      }
    }
  };

  return (
    <>
      {renderDialogs({
        confirm: {
          title: "フロントエンドアドオンの無効化",
          displayMessage:
            selectedEnabledAddons.length === 1
              ? `${selectedEnabledAddons[0].name}を無効化しますか？`
              : `${selectedEnabledAddons.length}件のフロントエンドアドオンを無効化しますか？`,
          buttonText: "無効化",
          inProgressButtonText: "無効化中...",
          isDisableButton: selectedEnabledAddons.length === 0,
          onClick: runDelete,
          isThemeRed: true,
        },
      })}
    </>
  );
};
